import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack,
  styled,
  TextField,
  TextFieldProps,
  Typography,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FormikForm from 'src/components/common/FormikForm';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import ModalComponent from 'src/components/Modal/ModalComponent';

// Utilities
import GuardContributeImg from 'src/asset/icons/guard-contribute.svg';
import {
  IPropsDataMergeProfile,
  TypeContributeCompany,
  TYPE_CONTRIBUTE,
} from 'src/features/CompanyDetail/Common/config';
import { useMutateContributeCompany } from 'src/services/mutate';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { convertSnakeCaseToUpperFirst, getCurrentUrl, getOriginUrl } from 'src/utils/helpers/stringHelpers';
import { MergeData } from 'src/zustand/useCompanyContribute';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { RELATIONSHIP_CONTRIBUTE_DATA } from 'src/features/Contributions/config';
import { AuthRouteKeys } from 'src/utils/constants/routes';

interface IProps {
  companyData: ICompanyDetail;
  open: boolean;
  mergeData?: MergeData;
  handleClose: () => void;
}

const StyledHeaderAlert = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.success.light,
  width: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  padding: '8px 32px',
}));

const StyledBoxData = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px dashed ${theme.palette.primary.main}`,
  position: 'relative',
  display: 'flex',
  padding: theme.spacing(2),
  flex: theme.spacing(1),
}));

const FormReportMergeProfile: React.FC<IProps> = ({ mergeData, companyData, open, handleClose }) => {
  const theme = useTheme();
  const { mutateAsync } = useMutateContributeCompany();

  const formik = useFormik<IPropsDataMergeProfile>({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      type_contribute: TypeContributeCompany.MergeCompanyProfile,
      relation_to_data: '',
      note: '',
      is_confirm: false,
      data_company_profile_url: getCurrentUrl(),
      data_company_name: companyData.name || '',
      merge_company_profile_id: mergeData?.merge_company_profile_id || '',
      merge_company_name: mergeData?.merge_company_name || '',
      merge_company_jurisdiction: mergeData?.merge_company_jurisdiction || '',
      merge_company_profile_type: getArrayDefault(mergeData?.merge_company_profile_type),
    },
    validationSchema: Yup.object().shape({
      type_contribute: Yup.string().max(255).required('Email is required'),
      relation_to_data: Yup.string().max(255).required('Relationship Data is required'),
      note: Yup.string().max(255),
      is_confirm: Yup.bool().oneOf([true], 'Please checked this checkbox'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const params: TypeDTOMergeProfile = {
        company_id: companyData.id,
        name: companyData?.name || '',
        note: values.note,
        type_of_contribution: values.type_contribute,
        relation_to_data: values.relation_to_data,
        data_company_profile_url: values.data_company_profile_url,
        data_company_name: values.data_company_name,
        merge_company_profile_id: values.merge_company_profile_id,
        merge_company_name: values.merge_company_name,
        merge_company_jurisdiction: values.merge_company_jurisdiction,
        merge_company_profile_type: values.merge_company_profile_type,
      };
      const res = await mutateAsync(params);
      if (res.status_code === SUCCESS_CODE.CREATE) {
        handleClose();
      }
      setSubmitting(false);
    },
  });

  return (
    <ModalComponent
      padding={4}
      pt={10}
      handleClose={handleClose}
      open={open}
      closeOnBackdropClick={false}
      disabledCloseIcon={formik.isSubmitting}
      showCloseIcon={true}
      headerAlert={
        <StyledHeaderAlert id="form-contribute-header-alert" direction="row" alignItems="center">
          <Box src={GuardContributeImg} component="img" width={24} mr={1} />
          <Typography variant="caption"> Your personal information will remain </Typography>
          <Button variant="text" color="success">
            100% confidential
          </Button>
        </StyledHeaderAlert>
      }
    >
      <FormikForm formik={formik}>
        <Box width={540}>
          <Typography textAlign={'center'} variant="h5">
            Report Wrong Data
          </Typography>
          <Typography textAlign={'center'} color={theme.palette.text.secondary} variant="body2">
            Please fill in the information in the form, we will confirm via your email.
          </Typography>
          <Box mt={4} />
          <Stack direction={'column'} spacing={2.5}>
            <TextField
              type="select"
              id="type_contribute"
              name="type_contribute"
              value={formik.values.type_contribute}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              size="small"
              select
              fullWidth
              disabled
              label="Type Contribute"
              error={Boolean(formik.touched.type_contribute && formik.errors.type_contribute)}
              helperText={Boolean(formik.touched.type_contribute) && formik.errors.type_contribute}
            >
              {TYPE_CONTRIBUTE.map((type) => (
                <MenuItem value={type.value} key={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
            <FormControl>
              <Autocomplete
                clearIcon={false}
                disabled
                options={[]}
                freeSolo
                multiple
                renderTags={(value) => value.map((option, index) => <Chip key={option + index} label={option} />)}
                value={[`${getCurrentUrl()}`]}
                renderInput={(params: TextFieldProps) => (
                  <TextField
                    {...params}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled
                    size="small"
                    fullWidth
                    label={
                      <span>
                        Profile Contribute (URL) <TooltipFormLabel title="Profile Contribute" />
                      </span>
                    }
                  />
                )}
              />
            </FormControl>
            <FormControl>
              <Autocomplete
                clearIcon={false}
                disabled
                options={[]}
                freeSolo
                multiple
                renderTags={(value) => value.map((option, index) => <Chip key={option + index} label={option} />)}
                value={[companyData?.name || '']}
                renderInput={(params: TextFieldProps) => (
                  <TextField
                    {...params}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    required
                    disabled
                    size="small"
                    fullWidth
                    label={
                      <span>
                        Company <TooltipFormLabel title="Company" />
                      </span>
                    }
                  />
                )}
              />
            </FormControl>
            <StyledBoxData>
              <Stack spacing={2} flex={1}>
                <Typography variant="subtitle1">Profile Should Be Merge Into</Typography>
                <FormControl>
                  <Autocomplete
                    clearIcon={false}
                    disabled
                    options={[]}
                    freeSolo
                    multiple
                    renderTags={(value) => value.map((option, index) => <Chip key={option + index} label={option} />)}
                    value={[
                      `${getOriginUrl()}/${AuthRouteKeys.CompanyDetail.replace(':id?', mergeData?.merge_company_profile_id || '')}`,
                    ]}
                    renderInput={(params: TextFieldProps) => (
                      <TextField
                        {...params}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        disabled
                        size="small"
                        fullWidth
                        label={
                          <span>
                            Profile Contribute (URL) <TooltipFormLabel title="Profile Contribute" />
                          </span>
                        }
                      />
                    )}
                  />
                </FormControl>
                <FormControl>
                  <Autocomplete
                    clearIcon={false}
                    disabled
                    options={[]}
                    freeSolo
                    multiple
                    renderTags={(value) => value.map((option, index) => <Chip key={option + index} label={option} />)}
                    value={[`${mergeData?.merge_company_name}`]}
                    renderInput={(params: TextFieldProps) => (
                      <TextField
                        {...params}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        disabled
                        size="small"
                        fullWidth
                        label={
                          <span>
                            Company <TooltipFormLabel title="Profile Contribute" />
                          </span>
                        }
                      />
                    )}
                  />
                </FormControl>
                <FormControl>
                  <Autocomplete
                    clearIcon={false}
                    disabled
                    options={[]}
                    freeSolo
                    multiple
                    renderTags={(value) => value.map((option, index) => <Chip key={option + index} label={option} />)}
                    value={[`${mergeData?.merge_company_jurisdiction}`]}
                    renderInput={(params: TextFieldProps) => (
                      <TextField
                        {...params}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        disabled
                        size="small"
                        fullWidth
                        label={
                          <span>
                            Jurisdiction <TooltipFormLabel title="Profile Contribute" />
                          </span>
                        }
                      />
                    )}
                  />
                </FormControl>
                <FormControl>
                  <Autocomplete
                    clearIcon={false}
                    disabled
                    options={[]}
                    freeSolo
                    multiple
                    renderTags={(value) => (
                      <Stack direction="row" gap={1}>
                        {value.map((option, index) => (
                          <Chip key={option + index} label={convertSnakeCaseToUpperFirst(option)} />
                        ))}
                      </Stack>
                    )}
                    value={getArrayDefault(mergeData?.merge_company_profile_type)}
                    renderInput={(params: TextFieldProps) => (
                      <TextField
                        {...params}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        disabled
                        size="small"
                        fullWidth
                        label={
                          <span>
                            Company Type <TooltipFormLabel title="Profile Contribute" />
                          </span>
                        }
                      />
                    )}
                  />
                </FormControl>
              </Stack>
            </StyledBoxData>
            <TextField
              type="select"
              id="relation_to_data"
              name="relation_to_data"
              value={formik.values.relation_to_data}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              size="small"
              select
              fullWidth
              required
              placeholder="Select option"
              label={
                <span>
                  Relationship to Data
                  <TooltipFormLabel title="Relationship to Data" />
                </span>
              }
              error={Boolean(formik.touched.relation_to_data && formik.errors.relation_to_data)}
              helperText={Boolean(formik.touched.relation_to_data) && formik.errors.relation_to_data}
            >
              {RELATIONSHIP_CONTRIBUTE_DATA.map((type) => (
                <MenuItem value={type.value} key={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="text"
              id="note"
              name="note"
              value={formik.values.note}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              size="small"
              fullWidth
              placeholder="Please describe detail hereSelect option"
              label={<span>Your Note To Change Data</span>}
              multiline
              rows={4}
              error={Boolean(formik.touched.note && formik.errors.note)}
              helperText={Boolean(formik.touched.note) && formik.errors.note}
            />
            <Box>
              <FormControlLabel
                id="is_confirm"
                name="is_confirm"
                onChange={(e, value) => {
                  formik.setFieldValue('is_confirm', value);
                }}
                value={formik.values.is_confirm}
                control={<Checkbox checked={formik.values.is_confirm} />}
                label={
                  <Typography color={theme.palette.text.secondary} variant="body1">
                    I certify that I am authorized to prove the above information
                  </Typography>
                }
              />
              {formik.touched.is_confirm && formik.errors.is_confirm && (
                <FormHelperText error>{formik.errors.is_confirm}</FormHelperText>
              )}
            </Box>
            <ButtonWithLoading
              fullWidth
              size="large"
              variant="contained"
              isRounded
              type="submit"
              isLoading={formik.isSubmitting}
              disabled={formik.isSubmitting}
            >
              Submit
            </ButtonWithLoading>
          </Stack>
        </Box>
      </FormikForm>
    </ModalComponent>
  );
};
export default FormReportMergeProfile;
